import React, { useState, useEffect, useRef } from 'react';
import { Modal, Select, Input, Button, Typography, Checkbox, Form, DatePicker, Divider, message } from 'antd';
import { CalendarDays, X, MapPin, Bell, Clock, Users as UsersIcon } from 'lucide-react';
import dayjs from 'dayjs';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;
const { TextArea } = Input;

const AppointmentModalV2 = ({ isOpen, onClose, record, users: propUsers = [] }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);

  const users = propUsers.length > 0 ? propUsers : internalUsers;

  const outcomes = [
    { label: 'Converted', value: '1' },
    { label: 'Wrong Number', value: '2' },
    { label: 'Dead Lead', value: '3' },
    { label: 'Not-Interested', value: '4' },
    { label: 'Call-Back', value: '5' },
  ];

  const fetchUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const response = await dialerService.getHierarchyUsers();
      if (response && Array.isArray(response)) {
        const mapped = response
          .filter(u => u && (u.Code || u.Value || u.Id || u.UserId || u.UserID))
          .map(u => ({
            label: u.Text || u.Name || u.UserName || u.label || 'Unknown User',
            value: String(u.Code || u.Value || u.Id || u.UserId || u.UserID)
          }));
        setInternalUsers(mapped);
      }
    } catch (e) {
      console.error('Modal fetch users error:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isOpen && propUsers.length === 0 && internalUsers.length === 0) {
      fetchUsers();
    }
  }, [isOpen, propUsers.length, internalUsers.length]);

  useEffect(() => {
    if (isOpen && record) {
      handleReset();

      // Initialize Google Places Autocomplete
      setTimeout(() => {
        const inputElement = document.getElementById('appointment-location-input');
        if (inputElement && window.google && window.google.maps && window.google.maps.places) {
          const autocomplete = new window.google.maps.places.Autocomplete(inputElement, { types: ['geocode'] });
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const formatted = place.formatted_address || place.name || '';
            form.setFieldsValue({
              location: formatted,
              address: formatted
            });
          });
        }
      }, 500);
    }
  }, [isOpen, record, users]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      const { userId } = getSession();
      const taskData = {
        Id: 0,
        Title: values.title,
        Description: values.description,
        Remarks: values.remarks,
        StartDate: values.startDate ? values.startDate.format('YYYY-MM-DD HH:mm:ss') : null,
        EndDate: values.endDate ? values.endDate.format('YYYY-MM-DD HH:mm:ss') : null,
        LeadId: record.Id,
        AssignedTo: Number(values.owner),
        CreatedBy: userId,
        IsTask: false,
        IsAppointment: true,
        CollaboratorUserIds: values.collaborators ? values.collaborators.join(',') : '',
        Address: values.location,
        CompleteAddress: values.address,
        Status: 'open'
      };

      const res = await dialerService.insertActivity(taskData);

      if (res && res.IsInserted) {
        message.success('Appointment scheduled successfully!');
        onClose();
      } else {
        message.error(res?.Message || 'Failed to save appointment');
      }
    } catch (error) {
      console.error('Failed to save appointment:', error);
      message.error('An error occurred while saving the appointment.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        title: `Appointment with ${record?.PersonName || record?.PersonMobileNo || record?.MobileNo || record?.ScheduleMobileNo || 'Customer'}`,
        startDate: dayjs().add(1, 'day').hour(10).minute(0),
        endDate: dayjs().add(1, 'day').hour(11).minute(0),
        owner: users && users.length > 0 ? users[0].value : undefined
      });
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <CalendarDays size={20} className="text-rose-600" />
          <span className="text-lg font-bold text-slate-800">Add Appointment</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={650}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="py-4 max-h-[75vh] overflow-y-auto px-2 custom-scrollbar"
      >
        <div className="space-y-4">
          <Form.Item
            name="title"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Title</span>}
            rules={[{ required: true, message: 'Please Enter Title' }]}
          >
            <Input placeholder="Please Enter Title" className="h-11 rounded-xl shadow-sm border-slate-200" />
          </Form.Item>

          <Form.Item
            name="description"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Description</span>}
          >
            <TextArea rows={2} placeholder="Start Typing the details about the appointment" className="rounded-xl shadow-sm border-slate-200" />
          </Form.Item>

          <Form.Item
            name="remarks"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Remarks</span>}
          >
            <TextArea rows={2} placeholder="about the task..." className="rounded-xl shadow-sm border-slate-200" />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="startDate"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Start Date and Time</span>}
              rules={[{ required: true, message: 'Select start time' }]}
            >
              <DatePicker
                showTime
                format="DD-MMM-YYYY HH:mm"
                className="w-full h-11 rounded-xl shadow-sm border-slate-200"
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>

            <Form.Item
              name="endDate"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> End Date and Time</span>}
              rules={[{ required: true, message: 'Select end time' }]}
            >
              <DatePicker
                showTime
                format="DD-MMM-YYYY HH:mm"
                className="w-full h-11 rounded-xl shadow-sm border-slate-200"
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="owner"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Owner</span>}
              rules={[{ required: true, message: 'Select owner' }]}
            >
              <Select
                placeholder="---Select Owner---"
                options={users}
                className="h-11 premium-select"
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="collaborators"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Collaborators</span>}
            >
              <Select
                mode="multiple"
                placeholder="Select Collaborators"
                options={users}
                className="min-h-11 premium-select"
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </div>

          <Divider orientation="left" className="!my-2">
            <span className="flex items-center gap-2 text-slate-400 font-bold text-[10px] uppercase tracking-widest">
              <MapPin size={12} /> Location Details
            </span>
          </Divider>

          <div className="grid grid-cols-1 gap-4">
            <Form.Item
              name="location"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Where</span>}
              rules={[{ required: true, message: 'Enter location' }]}
            >
              <Input
                id="appointment-location-input"
                prefix={<MapPin size={16} className="text-slate-400" />}
                placeholder="Enter location of appointment"
                className="h-11 rounded-xl shadow-sm border-slate-200"
              />
            </Form.Item>

            <Form.Item
              name="address"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Complete Address</span>}
            >
              <TextArea rows={2} placeholder="Please enter complete address." className="rounded-xl shadow-sm border-slate-200" />
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-10 border-t border-slate-100 pt-6">
          <Button
            onClick={handleReset}
            className="rounded-xl font-black text-[11px] uppercase tracking-widest h-12 px-8 border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-all shadow-sm"
          >
            Reset
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="rounded-xl font-black text-[11px] uppercase tracking-widest h-12 px-12 bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-200 border-none flex items-center gap-2 transition-all"
          >
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AppointmentModalV2;
